import React, { useState, useEffect, useContext } from 'react';
import { Container, Form, Button, Card, Row, Col, Alert, Table } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import api from '../../services/api';
import './Customer.css';

const CustomerSchedule = () => {
  const { custCode } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // 客戶資料
  const [customer, setCustomer] = useState(null);

  // 配送時間表
  const [schedules, setSchedules] = useState([]);

  // 編輯中的配送時間表
  const [editingSchedule, setEditingSchedule] = useState({
    div: '',
    rt_mon: 0,
    rt_tue: 0,
    rt_wed: 0,
    rt_thu: 0,
    rt_fri: 0,
    rt_sat: 0,
    rt_sun: 0
  });

  // 頁面狀態
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [canChange, setCanChange] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 確保只有管理員可以訪問
  useEffect(() => {
    if (user?.role !== 0) {
      navigate('/');
    }
  }, [user, navigate]);

  // 載入客戶資料和配送時間表
  useEffect(() => {
    if (custCode && user?.role === 0) {
      loadCustomerAndSchedules();
    }
  }, [custCode, user]);

  const loadCustomerAndSchedules = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/customers/${custCode}`);
      setCustomer(response.data.data.customer);
      setSchedules(response.data.data.schedules || []);

      // 重置編輯表單
      setEditingSchedule({
        div: schedules.length > 0 ? '' : '10', // 如果沒有時間表，預設為牛奶部門
        rt_mon: 0,
        rt_tue: 0,
        rt_wed: 0,
        rt_thu: 0,
        rt_fri: 0,
        rt_sat: 0,
        rt_sun: 0
      });

      setError('');
    } catch (err) {
      console.error('載入客戶資料失敗:', err);
      setError('載入客戶資料失敗: ' + (err.response?.data?.message || '請稍後再試'));
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleChange = (field, value) => {
    setEditingSchedule(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editingSchedule.div) {
      setError('請選擇部門');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const scheduleData = {
        ...editingSchedule,
        cust_code: custCode
      };

      await api.put(`/customers/${custCode}/schedule`, scheduleData);
      setSuccess('配送時間表已成功更新');

      // 重新載入配送時間表
      loadCustomerAndSchedules();
    } catch (err) {
      console.error('更新配送時間表失敗:', err);
      setError('更新配送時間表失敗: ' + (err.response?.data?.message || '請稍後再試'));
    } finally {
      setSaving(false);
      setCanChange(false);
    }
  };

  const handleEditSchedule = (schedule) => {
    setEditingSchedule({
      div: schedule.div.toString(),
      rt_mon: schedule.rt_mon,
      rt_tue: schedule.rt_tue,
      rt_wed: schedule.rt_wed,
      rt_thu: schedule.rt_thu,
      rt_fri: schedule.rt_fri,
      rt_sat: schedule.rt_sat,
      rt_sun: schedule.rt_sun
    });
    setCanChange(true);
  };

  const handleBack = () => {
    navigate('/customers');
  };

  // 獲取部門名稱
  const getDivisionName = (divCode) => {
    switch (divCode) {
      case '10': return '牛奶';
      default: return `部門(${divCode})`;
    }
  };

  return (
    <Container className="customer-schedule-container">
      <Row className="mb-3 align-items-center">
        <Col xs={8}>
          <h2>客戶配送時間表</h2>
          {customer && <p>客戶: {customer.cust_name} ({customer.cust_code})</p>}
        </Col>
        <Col xs={4} className="text-end">
          <Button variant="outline-secondary" onClick={handleBack}>
            返回客戶列表
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {loading ? (
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">載入中...</span>
          </div>
        </div>
      ) : (
        <>
          <Card className="mb-4">
            <Card.Header>現有配送時間表</Card.Header>
            <Card.Body>
              {schedules.length === 0 ? (
                <p className="text-center">尚未設置配送時間表</p>
              ) : (
                <Table responsive>
                  <thead>
                    <tr>
                      <th>部門</th>
                      <th>星期一</th>
                      <th>星期二</th>
                      <th>星期三</th>
                      <th>星期四</th>
                      <th>星期五</th>
                      <th>星期六</th>
                      <th>星期日</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedules.map((schedule, index) => (
                      <tr key={index}>
                        <td>{getDivisionName(schedule.div)}</td>
                        <td>{schedule.rt_mon ? '✓' : '-'}</td>
                        <td>{schedule.rt_tue ? '✓' : '-'}</td>
                        <td>{schedule.rt_wed ? '✓' : '-'}</td>
                        <td>{schedule.rt_thu ? '✓' : '-'}</td>
                        <td>{schedule.rt_fri ? '✓' : '-'}</td>
                        <td>{schedule.rt_sat ? '✓' : '-'}</td>
                        <td>{schedule.rt_sun ? '✓' : '-'}</td>
                        <td>
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => handleEditSchedule(schedule)}
                          >
                            編輯
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>{editingSchedule.div && schedules.some(s => s.div.toString() === editingSchedule.div) ? '編輯' : '新增'}配送時間表</Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>部門</Form.Label>
                      <Form.Select
                        value={editingSchedule.div}
                        onChange={(e) => handleScheduleChange('div', e.target.value)}
                        required
                      >
                        <option value="">選擇部門</option>
                        <option value="10">牛奶</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <div className="mb-3">
                  <Form.Label>配送日</Form.Label>
                  <div className="d-flex flex-wrap">
                    <Form.Check
                      type="checkbox"
                      id="rt_mon"
                      label="星期一"
                      className="me-3"
                      checked={editingSchedule.rt_mon === 1}
                      onChange={(e) => handleScheduleChange('rt_mon', e.target.checked ? 1 : 0)}
                    />
                    <Form.Check
                      type="checkbox"
                      id="rt_tue"
                      label="星期二"
                      className="me-3"
                      checked={editingSchedule.rt_tue === 1}
                      onChange={(e) => handleScheduleChange('rt_tue', e.target.checked ? 1 : 0)}
                    />
                    <Form.Check
                      type="checkbox"
                      id="rt_wed"
                      label="星期三"
                      className="me-3"
                      checked={editingSchedule.rt_wed === 1}
                      onChange={(e) => handleScheduleChange('rt_wed', e.target.checked ? 1 : 0)}
                    />
                    <Form.Check
                      type="checkbox"
                      id="rt_thu"
                      label="星期四"
                      className="me-3"
                      checked={editingSchedule.rt_thu === 1}
                      onChange={(e) => handleScheduleChange('rt_thu', e.target.checked ? 1 : 0)}
                    />
                    <Form.Check
                      type="checkbox"
                      id="rt_fri"
                      label="星期五"
                      className="me-3"
                      checked={editingSchedule.rt_fri === 1}
                      onChange={(e) => handleScheduleChange('rt_fri', e.target.checked ? 1 : 0)}
                    />
                    <Form.Check
                      type="checkbox"
                      id="rt_sat"
                      label="星期六"
                      className="me-3"
                      checked={editingSchedule.rt_sat === 1}
                      onChange={(e) => handleScheduleChange('rt_sat', e.target.checked ? 1 : 0)}
                    />
                    <Form.Check
                      type="checkbox"
                      id="rt_sun"
                      label="星期日"
                      className="me-3"
                      checked={editingSchedule.rt_sun === 1}
                      onChange={(e) => handleScheduleChange('rt_sun', e.target.checked ? 1 : 0)}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-end">
                  <Button
                    id='btn_postChange'
                    variant="primary"
                    type="submit"
                    disabled={!canChange | saving}
                  >
                    {saving ? '保存中...' : '保存配送時間表'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </>
      )}
    </Container>
  );
};

export default CustomerSchedule;